import type { ParentOfferContext } from "@/lib/studentParentOfferEvents";
import {
  CancellationFormRequest,
  CheckPaymentResult,
  FeatureDiscoveryData,
  FeatureDiscoveryKey,
  GetUserDataResult,
  Hoofdstuk,
  PauzeOptiesResponse,
  PersonalizationFormRequest,
  SavedHuiswerkSession,
  UserPreferences,
  StappenplanFormRequest,
  SubscriptionPauseResponse,
  SchoolRegio,
  PaymentOutcome
} from "@/lib/types";
import {
  AuthenticateResponse,
  GiftInviteStatus,
  GiftCheckoutResult,
  BegripvraagChatMessage,
  BegripvraagChatResponse,
  BegripvraagFeedbackResponse,
  GetAfsluiterResponse,
  SamenvattingResponse,
  ShareVraagResponse,
  TheorieGroep,
  TheorieResponse,
  VraagResponse,
  StappenplanResponse,
  SubvraagResponse,
  SubscriptionPlan,
  SubscriptionResponse
} from "./api_types";
import type { LifetimePurchaseStatus, LifetimeAction, CancellationResult } from "./api_types";
import { HoofdstukkenLijstResponse, Profiel } from "@/lib/profiel_types";
import {
  ParentStudentInvite,
  InviteCreateResponse,
  SubscriptionLink,
  ParentSubscription,
  UserRole
} from "@/lib/parent_student_types";
import { MathpixSession, MathpixStrokeResponse } from "@/hooks/useSketchCanvas";
import * as Sentry from "@sentry/nextjs";
import { Platform } from "@/hooks/useLayoutMode";
import { Surface } from "@/lib/surface";
import type { TourStatus } from "@/lib/tourState";
import { leesKlikIds, leesPaginaUrl } from "@/lib/metaClick";
import { attributionStatus, readAttribution } from "@/lib/attribution";
import { leesEersteAanraking, type FirstTouch } from "@/lib/firstTouch";

export class WisWizAPIError extends Error {
  public readonly statusCode: number;
  public readonly detail?: string;

  constructor(message: string, statusCode: number, detail?: string) {
    super(message);
    this.name = "WisWizAPIError";
    this.statusCode = statusCode;
    this.detail = detail;
    Object.setPrototypeOf(this, WisWizAPIError.prototype);
  }
}

/** Per-call reporting rule for apiFetch. */
export interface ApiFetchMeta {
  /**
   * Non-2xx statuses that are a designed answer of this endpoint (for example
   * 409 "not eligible", 401 "login required" on an optional-auth prefetch,
   * 429 on a daily cap). apiFetch still throws a WisWizAPIError for them, but
   * does not report them to Sentry (wiswiz #180).
   */
  expectedStatuses?: readonly number[];
}

export class WisWizAPI {
  async apiFetch<T>(
    url: string,
    options?: RequestInit,
    meta?: ApiFetchMeta
  ): Promise<T> {
    // Generic api fetch with better logging
    let response: Response;
    try {
      response = await fetch(url, options);
    } catch (err) {
      // Network errors (offline, connection reset) — not actionable, don't report
      throw new WisWizAPIError(
        `Network error: ${options?.method ?? "GET"} ${url}`,
        0
      );
    }

    if (!response.ok) {
      const body = await response.text().catch(() => null);
      let detail: string | undefined;
      if (body) {
        try {
          const parsed = JSON.parse(body) as { detail?: unknown };
          if (typeof parsed.detail === "string") {
            detail = parsed.detail;
          }
        } catch {
          // Non-JSON error bodies are still logged to Sentry below.
        }
      }

      // Legacy path allowlist, frozen: a designed non-2xx is declared at its
      // call site with meta.expectedStatuses (wiswiz #180), not added here.
      const isExpectedUnauthorized =
        response.status === 401 &&
        (url === "/api/users" ||
          url.startsWith("/api/content/vragen/") ||
          url === "/api/parent-student/links" ||
          url === "/api/parent-student/invites");
      const isExpectedRateLimit =
        response.status === 429 &&
        (url.startsWith("/api/content/stappenplannen/") ||
          url === "/api/hwm/samenvatting");
      const isExpectedPaymentRequired =
        response.status === 402 &&
        url.startsWith("/api/content/stappenplannen/");
      const isExpectedByCaller =
        meta?.expectedStatuses?.includes(response.status) ?? false;
      if (
        !isExpectedUnauthorized &&
        !isExpectedRateLimit &&
        !isExpectedPaymentRequired &&
        !isExpectedByCaller
      ) {
        Sentry.captureException(
          new Error(`API ${options?.method ?? "GET"} ${url} failed`),
          {
            extra: {
              url,
              method: options?.method ?? "GET",
              status: response.status,
              responseBody: body
            }
          }
        );
      }

      throw new WisWizAPIError(
        `API ${options?.method ?? "GET"} ${url} failed`,
        response.status,
        detail
      );
    }

    if (response.status === 204) {
      return undefined as T;
    }

    const text = await response.text();
    if (!text) {
      return undefined as T;
    }

    return JSON.parse(text) as T;
  }

  async getUser(): Promise<GetUserDataResult | undefined> {
    try {
      return await this.apiFetch<GetUserDataResult>("/api/users", {
        method: "GET",
        credentials: "include"
      });
    } catch (e) {
      if (e instanceof WisWizAPIError && e.statusCode === 401) {
        return undefined;
      }
      throw e;
    }
  }

  async updateTourStatus(status: TourStatus): Promise<void> {
    await this.apiFetch("/api/users/tour-status", {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status })
    });
  }

  async updateUserPreferences(
    preferences: Partial<UserPreferences>
  ): Promise<void> {
    await this.apiFetch("/api/users/preferences", {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(preferences)
    });
  }

  async markFeatureDiscovered(
    featureKey: FeatureDiscoveryKey,
    body: { source: string; metadata?: Record<string, unknown> }
  ): Promise<FeatureDiscoveryData> {
    return await this.apiFetch<FeatureDiscoveryData>(
      `/api/users/feature-discoveries/${featureKey}`,
      {
        method: "PUT",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          source: body.source,
          metadata: body.metadata ?? {}
        })
      }
    );
  }

  async devLogin(email: string) {
    // 200
    await this.apiFetch("/api/development/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email })
    });
  }

  async completeRegistration(
    registrationToken: string,
    role: UserRole,
    options?: { firstName?: string; lastName?: string }
  ): Promise<AuthenticateResponse> {
    return await this.apiFetch<AuthenticateResponse>(
      "/api/auth/complete-registration",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          registration_token: registrationToken,
          role,
          ...(options?.firstName && { first_name: options.firstName }),
          ...(options?.lastName && { last_name: options.lastName }),
          // #290: the first touch of this browser lands on the new user row
          first_touch: leesEersteAanraking()
        })
      }
    );
  }

  /**
   * Trade the one-time code from the Apple callback redirect for the session
   * cookie. The code is single-use and expires 2 minutes after the backend
   * minted it.
   */
  async exchangeCode(code: string): Promise<void> {
    await this.apiFetch("/api/auth/exchange-token", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ code })
    });
  }

  async logout(): Promise<void> {
    // 204
    await this.apiFetch("/api/auth/logout", {
      method: "POST",
      credentials: "include"
    });
  }

  async deleteUser(): Promise<void> {
    // 204
    await this.apiFetch("/api/users", {
      method: "DELETE",
      credentials: "include"
    });
  }

  async createSubscription(
    platform: Platform,
    invite_token?: string,
    surface?: Surface | null,
    plan_id?: string,
    request_id?: string
  ): Promise<SubscriptionResponse | undefined> {
    // #124: de Facebook-klik-id's reizen mee met de betaling. De Purchase die
    // Meta krijgt wordt later gebouwd in de Mollie-webhook, zonder browser en
    // zonder cookies; zonder deze twee kan Meta de verkoop nooit aan de
    // advertentie koppelen. Ontbreken ze (geen klik, adblocker), dan gaan ze
    // als null mee en verandert er niets aan de betaling.
    const { fbc, fbp } = leesKlikIds();
    const page_url = leesPaginaUrl();
    const body: {
      platform: Platform;
      invite_token: string | null;
      surface: Surface | null;
      plan_id?: string;
      request_id?: string;
      fbc: string | null;
      fbp: string | null;
      page_url: string | null;
      attribution: Record<string, string>;
      attribution_status: "attributed" | "unknown";
      first_touch: FirstTouch | null;
    } = {
      platform,
      invite_token: invite_token ?? null,
      surface: surface ?? null,
      fbc,
      fbp,
      page_url,
      attribution: readAttribution(),
      attribution_status: attributionStatus(),
      // #290: the first touch of this browser (landing utm, fbclid, referrer)
      first_touch: leesEersteAanraking()
    };

    if (plan_id) body.plan_id = plan_id;
    if (request_id) body.request_id = request_id;

    return await this.apiFetch<SubscriptionResponse>(
      "/api/subscription/create",
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      }
    );
  }

  async createGiftSubscription(
    email: string,
    platform: Platform,
    surface?: Surface | null,
    plan_id?: string,
    children?: Record<string, never>[],
    request_id?: string,
    supersedes_request_id?: string,
    parent_offer_context?: ParentOfferContext,
    referral_receipt?: string
  ): Promise<GiftCheckoutResult> {
    if (children && (!request_id || children.length < 1 || children.length > 3)) {
      throw new Error("Een bestelling vereist 1 tot 3 kinderen en een verzoeknummer.");
    }
    // #124: dit IS het pad van de oudercampagne — /voor-ouders/start is een
    // anonieme gift-checkout. De klik-id's moeten dus juist hier meereizen.
    const { fbc, fbp } = leesKlikIds();
    const page_url = leesPaginaUrl();
    const body: {
      email: string;
      platform: Platform;
      surface: Surface | null;
      plan_id?: string;
      fbc: string | null;
      fbp: string | null;
      page_url: string | null;
      attribution: Record<string, string>;
      attribution_status: "attributed" | "unknown";
      first_touch: FirstTouch | null;
      children?: Record<string, never>[];
      request_id?: string;
      supersedes_request_id?: string;
      parent_offer_context?: ParentOfferContext;
      referral_receipt?: string;
      parent_ad_context: { consent_state: "unknown" };
    } = {
      email,
      platform,
      surface: surface ?? null,
      fbc,
      fbp,
      page_url,
      attribution: readAttribution(),
      attribution_status: attributionStatus(),
      // #290: the first touch of this browser (landing utm, fbclid, referrer)
      first_touch: leesEersteAanraking(),
      parent_ad_context: { consent_state: "unknown" }
    };

    if (referral_receipt) body.referral_receipt = referral_receipt;
    if (parent_offer_context) body.parent_offer_context = parent_offer_context;
    if (plan_id) body.plan_id = plan_id;
    if (children) {
      body.children = children;
    }
    if (request_id) body.request_id = request_id;
    if (supersedes_request_id) body.supersedes_request_id = supersedes_request_id;

    return await this.apiFetch<GiftCheckoutResult>(
      "/api/subscription/gift/create",
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify(body)
      }
    );
  }

  async getGiftInvite(token: string, parentKey?: string | null): Promise<GiftInviteStatus> {
    const query = parentKey ? `?parent_key=${encodeURIComponent(parentKey)}` : "";
    return await this.apiFetch(`/api/subscription/gift/${token}${query}`, {
      method: "GET"
    });
  }

  async cancelGiftSubscription(token: string, parentKey: string): Promise<CancellationResult & { subscription_status?: string }> {
    return this.apiFetch(`/api/subscription/gift/${encodeURIComponent(token)}/cancel`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ parent_key: parentKey })
    }, { expectedStatuses: [400, 403, 404, 409, 410, 422] });
  }

  async claimGiftSubscription(token: string): Promise<{ ok: boolean }> {
    return await this.apiFetch<{ ok: boolean }>(
      `/api/subscription/gift/${token}/claim`,
      {
        method: "POST",
        credentials: "include"
      }
    );
  }

  async resumeSubscription(
    platform: Platform,
    subscriptionId: string,
    surface?: Surface | null,
    plan_id?: string,
    request_id?: string
  ): Promise<SubscriptionResponse | undefined> {
    const params = new URLSearchParams({
      subscription_id: subscriptionId,
      platform
    });
    if (surface) params.set("surface", surface);
    if (plan_id) params.set("plan_id", plan_id);
    // #169: een hervatting stuurde geen klik-context mee, dus de Purchase
    // ervan kon nooit aan een advertentie worden gekoppeld. Zelfde drie velden
    // als bij createSubscription; ontbreken ze, dan gaan ze als null mee.
    const { fbc, fbp } = leesKlikIds();
    const page_url = leesPaginaUrl();
    // #290: the first touch rides along too, like on the create routes
    const first_touch = leesEersteAanraking();
    return await this.apiFetch<SubscriptionResponse>(
      `/api/subscription/resume?${params.toString()}`,
      {
        method: "POST",
        credentials: "include",
        headers: {
          "Content-Type": "application/json"
        },
        body: JSON.stringify({ fbc, fbp, page_url, request_id, first_touch })
      }
    );
  }

  async getPlans(): Promise<SubscriptionPlan[]> {
    return await this.apiFetch<SubscriptionPlan[]>("/api/subscription/plans", {
      method: "GET",
      credentials: "include"
    });
  }

  async cancelSubscription(id: string): Promise<CancellationResult | undefined> {
    return this.apiFetch(`/api/subscription/cancel/${id}`, {
      method: "POST",
      credentials: "include"
    });
  }

  async getLifetimePurchase(id: string): Promise<LifetimePurchaseStatus> {
    return this.apiFetch(`/api/subscription/lifetime/${encodeURIComponent(id)}`, {
      credentials: "include"
    }, { expectedStatuses: [401, 403, 404] });
  }

  async recoverLifetimePurchase(id: string, action: LifetimeAction, platform: Platform): Promise<SubscriptionResponse> {
    return this.apiFetch(`/api/subscription/lifetime/${encodeURIComponent(id)}/recover`, {
      method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ action, platform })
    }, { expectedStatuses: [401, 403, 404, 409] });
  }

  async recoverLifetimeGift(token: string, parentKey: string, action: LifetimeAction, platform: Platform): Promise<SubscriptionResponse> {
    return this.apiFetch(`/api/subscription/gift/${encodeURIComponent(token)}/recover`, {
      method: "POST", headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ parent_key: parentKey, action, platform })
    }, { expectedStatuses: [403, 404, 409] });
  }

  async pauseSubscription(
    subscription_id: string,
    region: SchoolRegio
  ): Promise<SubscriptionPauseResponse | undefined> {
    const body = {
      subscription_id,
      region
    };

    return await this.apiFetch(`/api/subscription/pause`, {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body)
    });
  }

  async getPauzeerOpties(): Promise<PauzeOptiesResponse> {
    return await this.apiFetch(`/api/subscription/pause_options`);
  }

  /* wiswiz #167: de uitkomst van één betaling ZONDER inlog (alleen de status, tr_-id is de sleutel). Voor
     /payment/result als de inlogcookie na de Mollie-hop weg is: dan zegt de pagina de echte status, niet "gelukt". */
  async getPaymentOutcome(
    transaction_id: string
  ): Promise<{ id: string; outcome: PaymentOutcome }> {
    return await this.apiFetch<{ id: string; outcome: PaymentOutcome }>(
      `/api/payment/outcome?tr=${encodeURIComponent(transaction_id)}`,
      { method: "GET" }
    );
  }

  async getPaymentStatus(transaction_id: string): Promise<CheckPaymentResult> {
    return await this.apiFetch<CheckPaymentResult>(
      `/api/payment/status?tr=${transaction_id}`,
      {
        method: "GET",
        credentials: "include"
      }
    );
  }

  async shareVraag(id: number): Promise<ShareVraagResponse> {
    return await this.apiFetch<ShareVraagResponse>(
      `/api/content/vragen/${id}/share`,
      {
        method: "POST",
        credentials: "include"
      }
    );
  }

  async postPersonalizationForm(
    form: PersonalizationFormRequest
  ): Promise<void> {
    await this.apiFetch("/api/subscription/personalize", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form)
    });
  }

  async postCancellationForm(form: CancellationFormRequest): Promise<void> {
    // 204
    await this.apiFetch("/api/subscription/cancel_form", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form)
    });
  }

  async postStappenplanForm(form: StappenplanFormRequest): Promise<void> {
    // 204
    await this.apiFetch("/api/content/subvragen/feedback", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(form)
    });
  }

  async getVraag(id: number, shareToken?: string): Promise<VraagResponse> {
    // Keep cookies on share-token requests: the token grants access to the
    // vraag, while the auth cookie owns user-scoped state like foto sessions.
    return await this.apiFetch<VraagResponse>(
      shareToken
        ? `/api/content/vragen/${id}?share_token=${shareToken}`
        : `/api/content/vragen/${id}`,
      { method: "GET", credentials: "include" }
    );
  }

  async getStappenplan(
    id: number,
    shareToken?: string
  ): Promise<StappenplanResponse | false | "rate_limited"> {
    try {
      return await this.apiFetch<StappenplanResponse>(
        shareToken
          ? `/api/content/stappenplannen/${id}?share_token=${shareToken}`
          : `/api/content/stappenplannen/${id}`,
        { method: "GET", credentials: "include" }
      );
    } catch (e: any) {
      // Is WisWizAPIError
      if (e.statusCode === 402) return false;
      if (e.statusCode === 429) return "rate_limited";
      throw e;
    }
  }

  async getSubvraag(id: number): Promise<SubvraagResponse> {
    return await this.apiFetch<SubvraagResponse>(
      `/api/content/subvragen/${id}`,
      { method: "GET", credentials: "include" }
    );
  }

  async getTheorie(
    id: number,
    fromStappenplan: number
  ): Promise<TheorieResponse> {
    const params = new URLSearchParams({
      from_stappenplan: String(fromStappenplan)
    });
    // No expectedStatuses here: a 401 on a theorie is either "no entitlement"
    // or "stappenplan not opened first", and the second one only happens
    // through a frontend bug, so it must keep reporting.
    return await this.apiFetch<TheorieResponse>(
      `/api/content/theorieen/${id}?${params.toString()}`,
      { method: "GET", credentials: "include" }
    );
  }

  async getHoofdstukkenList(
    profiel: Profiel
  ): Promise<HoofdstukkenLijstResponse> {
    const params = new URLSearchParams({
      jaar: profiel.jaar.toString(),
      editie: profiel.editie.toString(),
      methode: profiel.methode,
      niveau: profiel.niveau
    });

    if (profiel.wiskundeType) {
      params.append("wiskunde_type", profiel.wiskundeType);
    }

    return await this.apiFetch<HoofdstukkenLijstResponse>(
      `/api/content/hoofdstukken?${params.toString()}`,
      { method: "GET" }
    );
  }

  async getHoofdstuk(id: number): Promise<Hoofdstuk> {
    return this.apiFetch<Hoofdstuk>(`/api/content/hoofdstukken/get/${id}`, {
      method: "GET",
      credentials: "include"
    });
  }

  async groepeerVragen(vraagIds: number[]): Promise<TheorieGroep[]> {
    return this.apiFetch<TheorieGroep[]>(
      "/api/hwm/groepeer",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ vraag_ids: vraagIds })
      },
      // Fires on every hoofdstuk open; 401 = no (valid) login cookie.
      { expectedStatuses: [401] }
    );
  }

  async getAfsluiter(
    subvraagId: number,
    shareToken?: string
  ): Promise<GetAfsluiterResponse> {
    return this.apiFetch<GetAfsluiterResponse>(
      "/api/hwm/afsluiter",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subvraag_id: subvraagId,
          ...(shareToken && { share_token: shareToken })
        })
      },
      // Prefetch on every subvraag load: 401 = login required / freemium
      // without unlock, 429 = daily stappenplan cap (both designed). A 502
      // (AI output not parseable) is a real failure and stays reported.
      { expectedStatuses: [401, 429] }
    );
  }

  async postBegripvraagFeedback(
    subvraagId: number,
    antwoord: string,
    shareToken?: string
  ): Promise<BegripvraagFeedbackResponse> {
    return this.apiFetch<BegripvraagFeedbackResponse>(
      "/api/hwm/afsluiter/feedback",
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({
          subvraag_id: subvraagId,
          antwoord,
          ...(shareToken && { share_token: shareToken })
        })
      }
    );
  }

  async postBegripvraagChat(
    subvraagId: number,
    question: string,
    options?: {
      studentAnswer?: string;
      feedback?: string;
      conversation?: BegripvraagChatMessage[];
      shareToken?: string;
    }
  ): Promise<BegripvraagChatResponse> {
    return this.apiFetch<BegripvraagChatResponse>("/api/hwm/afsluiter/chat", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({
        subvraag_id: subvraagId,
        question,
        ...(options?.studentAnswer && {
          student_answer: options.studentAnswer
        }),
        ...(options?.feedback && { feedback: options.feedback }),
        ...(options?.conversation && { conversation: options.conversation }),
        ...(options?.shareToken && { share_token: options.shareToken })
      })
    });
  }

  async hwmSamenvatting(groepId: number): Promise<SamenvattingResponse> {
    // The backend caches results after a successful generation. If the dev
    // proxy drops the connection mid-OpenAI call (Docker vpnkit idle timeout,
    // gateway 502, etc.), the backend still finishes and caches — a retry
    // then returns the cached result in milliseconds.
    const attempt = () =>
      this.apiFetch<SamenvattingResponse>("/api/hwm/samenvatting", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ groep_id: groepId }),
        signal: AbortSignal.timeout(300_000)
      });

    try {
      return await attempt();
    } catch (err) {
      if (err instanceof WisWizAPIError && err.statusCode !== 429) {
        return await attempt();
      }
      throw err;
    }
  }

  // ---- PARENT STUDENT ENDPOINTS ----
  async getInvite(token: string): Promise<ParentStudentInvite> {
    return await this.apiFetch<ParentStudentInvite>(
      `/api/parent-student/invite/${token}`,
      {
        method: "GET",
        credentials: "include"
      }
    );
  }

  async getInvites(): Promise<ParentStudentInvite[]> {
    return await this.apiFetch<ParentStudentInvite[]>(
      `/api/parent-student/invites`,
      {
        method: "GET",
        credentials: "include"
      }
    );
  }

  async getLinks(): Promise<SubscriptionLink[]> {
    return await this.apiFetch<SubscriptionLink[]>(
      `/api/parent-student/links`,
      {
        method: "GET",
        credentials: "include"
      }
    );
  }

  async postCancelInvite(id: number): Promise<boolean> {
    await this.apiFetch<void>(`/api/parent-student/invite/cancel/${id}`, {
      method: "POST",
      credentials: "include"
    });

    return true;
  }

  // Invite a new parent (student generates invite token). Direct fetch
  async postStudentInviteParent(): Promise<InviteCreateResponse> {
    const res = await fetch(`/api/parent-student/invite/parent`, {
      method: "POST",
      credentials: "include"
    });

    if (!res.ok) {
      let errorMessage =
        "Er ging iets mis bij het aanmaken van de uitnodiging.";
      try {
        const data = await res.json();
        if (data?.detail) errorMessage = data.detail;
      } catch {}
      throw new Error(errorMessage);
    }

    return (await res.json()) as InviteCreateResponse;
  }

  async redeemInvite(token: string): Promise<void> {
    await this.apiFetch<void>(`/api/parent-student/invite/redeem/${token}`, {
      method: "POST",
      credentials: "include"
    });
  }

  async getParentSubscription(
    subscriptionId: string
  ): Promise<ParentSubscription> {
    return await this.apiFetch<ParentSubscription>(
      `/api/subscription/parent/${subscriptionId}`,
      {
        method: "GET",
        credentials: "include"
      }
    );
  }

  async saveHuiswerkSet(session: SavedHuiswerkSession): Promise<void> {
    await this.apiFetch<void>("/api/users/huiswerk-set", {
      method: "PUT",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ data: session })
    });
  }

  async deleteHuiswerkSet(): Promise<void> {
    await this.apiFetch<void>("/api/users/huiswerk-set", {
      method: "DELETE",
      credentials: "include"
    });
  }

  async completeHuiswerkSet(): Promise<void> {
    await this.apiFetch<void>("/api/users/huiswerk-set/complete", {
      method: "POST",
      credentials: "include"
    });
  }

  async postProfiel(profiel: Profiel): Promise<void> {
    await this.apiFetch<void>("/api/users/profiel", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(profiel)
    });
  }

  async deleteProfiel(): Promise<void> {
    await this.apiFetch<void>("/api/users/profiel", {
      method: "DELETE",
      credentials: "include"
    });
  }

  // Influencer attribution
  async checkInfluencerSlug(slug: string): Promise<boolean> {
    try {
      await this.apiFetch(
        `/api/influencers/check/${slug}`,
        { method: "GET" },
        // Every unknown lowercase path (/login, /admin, scanner probes) lands
        // on the [slug] route and asks this; 404 = not an influencer.
        { expectedStatuses: [404] }
      );
      return true;
    } catch {
      return false;
    }
  }

  async setInfluencerAttribution(slug: string): Promise<void> {
    await this.apiFetch("/api/influencers/attribute", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ influencer_slug: slug })
    });
  }

  // Tutor chat history
  async getChatHistory(subvraagId: number): Promise<
    {
      step_index: number;
      messages: {
        id: number;
        question: string;
        answer: string;
        foto_session_id: string | null;
        photos: {
          image_id: number;
          position: number;
        }[];
      }[];
    }[]
  > {
    return this.apiFetch(`/api/tutor/chat-history?subvraag_id=${subvraagId}`, {
      method: "GET",
      credentials: "include"
    });
  }

  async getFotoImageUrl(imageId: number): Promise<string> {
    const response = await this.apiFetch<{ image_url: string }>(
      `/api/tutor/foto/image/${imageId}`,
      { method: "GET", credentials: "include" }
    );
    return response.image_url;
  }

  async deleteChatMessage(messageId: number): Promise<void> {
    await this.apiFetch(`/api/tutor/chat-history/${messageId}`, {
      method: "DELETE",
      credentials: "include"
    });
  }

  // Mathpix
  async createMathpixSession(): Promise<MathpixSession> {
    return this.apiFetch<MathpixSession>("/api/mathpix/session", {
      method: "POST",
      headers: { "Content-Type": "application/json" }
    });
  }

  async recognizeStrokes(
    app_token: string,
    strokes_session_id: string,
    strokes: { x: number[][]; y: number[][] }
  ): Promise<MathpixStrokeResponse> {
    const response = await fetch("https://api.mathpix.com/v3/strokes", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        app_token: app_token
      },
      body: JSON.stringify({
        strokes_session_id,
        strokes: {
          strokes: {
            x: strokes.x,
            y: strokes.y
          }
        }
      })
    });

    if (!response.ok) {
      const text = await response.text();
      throw new Error(`Mathpix error: ${text}`);
    }

    const data = await response.json();

    return {
      latex: data.latex_styled || data.latex,
      latex_confidence: data.latex_confidence
    };
  }

  async startHappyUserMarketingMessage(): Promise<{ message_id: number }> {
    return this.apiFetch(
      "/api/happy-user-marketing-message/start",
      { method: "POST", credentials: "include" },
      // 409 = "not eligible": the backend's designed answer for most students
      // on every stappenplan arrival (wiswiz #180: 2.9k Sentry errors/week).
      { expectedStatuses: [409] }
    );
  }

  async dismissHappyUserMarketingMessage(messageId: number): Promise<void> {
    await this.apiFetch("/api/happy-user-marketing-message/dismiss", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ message_id: messageId })
    });
  }
}
